/**
 * Agent Action Handler Registry
 *
 * Central configuration for all agent action handlers.
 * This file maps action identifiers to their handler functions.
 *
 * **Architecture Pattern**: Action-Based Metadata Handler
 * - Agents return events with type="action" and content=ACTION_CODE
 * - This registry maps action codes to handler functions
 * - Handlers emit EventBus events to trigger UI components
 *
 * **Classification Flags**:
 * - isCanvasAction: Actions that display components in the left canvas panel
 * - isControlMessage: Messages that should be filtered from chat display
 *
 * For complete documentation, see: /docs/architecture/agent-action-handler-pattern.md
 *
 * @module config/agentActions
 */

import {
  AgentAction,
  ChatContext,
  ReportPreviewPayload,
  HandoffPayload,
  POPreviewPayload,
  ChartPayload,
  FormPayload,
  isMetadataWithAction,
  isReportPreviewPayload,
  isHandoffPayload,
  isPOPreviewPayload,
} from '@/models/agentActions';
import { EventBus } from '@/main';

/**
 * Factory for creating canvas action handlers.
 *
 * Canvas actions are display-only actions where the canvas component
 * reads data directly from chatResponse.actions/events. The handler
 * is intentionally lightweight (just logging) since no processing is needed.
 *
 * @param actionType - The action type identifier (e.g., 'DISPLAY_SUPPLIERS_LIST')
 * @param description - Human-readable description of the action
 * @returns AgentAction object configured for canvas display
 */
function createCanvasActionHandler(actionType: string, description: string): AgentAction {
  return {
    type: actionType,
    isCanvasAction: true,
    isControlMessage: false,
    handler: (_payload: any, _context: ChatContext) => {
      console.log(`[AgentActions] ${actionType} - canvas component reads from chatResponse directly`);
    },
    description,
  };
}

/**
 * Central registry of all agent action handlers
 *
 * **To add a new action**:
 * 1. Add handler definition here with unique action key
 * 2. Add corresponding EventBus listener in target component
 * 3. Update backend agent to return metadata with this action
 * 4. Document in /docs/adding-new-agent-actions.md
 *
 * **Naming Convention**: Use kebab-case for action names (e.g., 'show-report-preview')
 *
 * @example
 * ```typescript
 * // Backend returns:
 * { action: 'show_report_preview', payload: {...} }
 *
 * // This registry executes the corresponding handler
 * agentActionHandlers['show_report_preview'].handler(payload, context);
 * ```
 */
export const agentActionHandlers: Record<string, AgentAction> = {
  /**
   * Report Builder: Show report preview with extracted parameters
   * (Legacy lowercase format - kept for backwards compatibility)
   *
   * **Emits**: EventBus event 'report-builder:preview'
   * **Target Component**: ReportBuilder.vue
   * **Backend Agent**: Report Builder Agent
   *
   * @param payload - ReportPreviewPayload with extracted_parameters
   * @param context - ChatContext with session and agent info
   */
  show_report_preview: {
    type: 'show_report_preview',
    loadingMessage: 'Loading report preview...',
    completionEvent: 'report-builder:preview-complete',
    isCanvasAction: true,
    isControlMessage: false,
    handler: (payload: ReportPreviewPayload, context: ChatContext) => {
      console.log('[AgentActions] Executing show_report_preview', {
        agent: context.agentId,
        session: context.sessionId,
        confidence: payload.extracted_parameters?.confidence_score,
      });

      // Emit EventBus event for ReportBuilder component to listen
      EventBus.$emit('report-builder:preview', payload.extracted_parameters);

      // Optional: Show success notification if high confidence
      if (payload.extracted_parameters?.confidence_score > 0.8) {
        // Note: Toast notifications can be added here if needed
        // Toast.success('Report preview ready!');
      }

      // Track analytics event
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('Report Preview Generated', {
          agent: context.agentId,
          measure: payload.extracted_parameters?.parameters?.measure,
          dimension: payload.extracted_parameters?.parameters?.dimension,
          confidence: payload.extracted_parameters?.confidence_score,
          column_count: payload.extracted_parameters?.parameters?.columns?.length || 0,
        });
      }
    },
    description: 'Display report preview with extracted parameters in ReportBuilder component',
  },

  /**
   * Report Builder: Show report preview (standardized uppercase format)
   *
   * **Emits**: EventBus event 'report-builder:preview'
   * **Target Component**: ReportBuilder.vue
   * **Backend Agent**: Report Builder Agent
   *
   * @param payload - ShowReportPreviewPayload from backend
   * @param context - ChatContext with session and agent info
   */
  SHOW_REPORT_PREVIEW: {
    type: 'SHOW_REPORT_PREVIEW',
    loadingMessage: 'Loading report preview...',
    completionEvent: 'report-builder:preview-complete',
    isCanvasAction: true,
    isControlMessage: false,
    handler: (payload: any, context: ChatContext) => {
      console.log('[AgentActions] Executing SHOW_REPORT_PREVIEW', {
        agent: context.agentId,
        session: context.sessionId,
        confidence: payload?.confidence_score,
      });

      // The payload comes directly from ShowReportPreviewPayload (extracted_parameters is embedded)
      const previewData = payload?.extracted_parameters || payload;

      // Emit EventBus event for ReportBuilder component to listen
      EventBus.$emit('report-builder:preview', previewData);

      // Compute normalized columnCount: prefer previewData.column_count, fallback to columns array length
      const columnsArray = payload?.extracted_parameters?.parameters?.columns;
      const columnCount = typeof previewData?.column_count === 'number'
        ? previewData.column_count
        : (Array.isArray(columnsArray) ? columnsArray.length : 0);

      // Track analytics event
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('Report Preview Generated', {
          agent: context.agentId,
          measure: previewData?.measure,
          dimension: previewData?.dimension,
          confidence: previewData?.confidence_score,
          column_count: columnCount,
        });
      }
    },
    description: 'Display report preview with extracted parameters in ReportBuilder component (standardized format)',
  },

  /**
   * PO Agent: Show PO preview with dynamic components (legacy handler)
   *
   * **Emits**: EventBus event 'po-builder:preview'
   * **Target Component**: PurchaseAssistant.vue
   * **Backend Agent**: Purchase Order Agent
   *
   * @param payload - POPreviewPayload with ChatResponse
   * @param context - ChatContext with session and agent info
   */
  show_po_agent_preview: {
    type: 'show_po_agent_preview',
    loadingMessage: 'Loading PO preview...',
    completionEvent: 'po-builder:preview-complete',
    isCanvasAction: true,
    isControlMessage: false,
    handler: (payload: POPreviewPayload, context: ChatContext) => {
      console.log('[AgentActions] ========================================');
      console.log('[AgentActions] Executing show_po_agent_preview');
      console.log('[AgentActions] Context:', {
        agent: context.agentId,
        session: context.sessionId,
        current_agent: payload.chat_response?.current_agent,
      });
      console.log('[AgentActions] Payload:', payload);
      console.log('[AgentActions] payload.chat_response:', payload.chat_response);
      console.log('[AgentActions] payload.chat_response.actions:', payload.chat_response?.actions);
      console.log('[AgentActions] ========================================');

      // Emit EventBus event for PurchaseAssistant component to listen
      console.log('[AgentActions] Emitting po-builder:preview event with:', payload.chat_response);
      EventBus.$emit('po-builder:preview', payload.chat_response);
      console.log('[AgentActions] Event emitted successfully');

      // Track analytics event
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('PO Preview Generated', {
          agent: context.agentId,
          current_agent: payload.chat_response?.current_agent,
          message_count: payload.chat_response?.messages?.length || 0,
        });
      }
    },
    description: 'Display PO preview with dynamic components in Purchase Assistant',
  },

  // ===========================================
  // PO Agent Individual Canvas Actions
  // ===========================================

  // ===========================================================================
  // Canvas Display Actions (PO Agent)
  //
  // These actions trigger canvas component displays. The handlers are
  // intentionally lightweight - canvas components read data directly from
  // chatResponse.actions/events rather than through handler processing.
  // ===========================================================================

  /** Display suppliers list - Target: SuppliersListDisplay.vue */
  DISPLAY_SUPPLIERS_LIST: createCanvasActionHandler(
    'DISPLAY_SUPPLIERS_LIST',
    'Display suppliers list in Purchase Assistant canvas',
  ),

  /** Display buying table - Target: BuyingTableDisplay.vue */
  DISPLAY_BUYING_TABLE: createCanvasActionHandler(
    'DISPLAY_BUYING_TABLE',
    'Display buying table in Purchase Assistant canvas',
  ),

  /** Display purchase order - Target: PurchaseOrderDisplay.vue */
  DISPLAY_PURCHASE_ORDER: createCanvasActionHandler(
    'DISPLAY_PURCHASE_ORDER',
    'Display purchase order in Purchase Assistant canvas',
  ),

  /** Display purchase order list - Target: PurchaseOrderListDisplay.vue */
  DISPLAY_PURCHASE_ORDER_LIST: createCanvasActionHandler(
    'DISPLAY_PURCHASE_ORDER_LIST',
    'Display purchase order list in Purchase Assistant canvas',
  ),

  /** Display reorder suggestions - Target: ReorderSuggestionsDisplay.vue */
  DISPLAY_REORDER_SUGGESTIONS: createCanvasActionHandler(
    'DISPLAY_REORDER_SUGGESTIONS',
    'Display reorder suggestions in Purchase Assistant canvas',
  ),

  /** Display PO draft - Target: PODraftDisplay.vue */
  DISPLAY_PO_DRAFT: createCanvasActionHandler(
    'DISPLAY_PO_DRAFT',
    'Display PO draft in Purchase Assistant canvas',
  ),

  /** Display draft PO preview (from AI Review) - Target: PurchaseOrderDisplay.vue */
  DISPLAY_DRAFT_PO_PREVIEW: createCanvasActionHandler(
    'DISPLAY_DRAFT_PO_PREVIEW',
    'Display draft PO preview in Purchase Assistant canvas',
  ),

  /** Display product analysis - Target: ProductAnalysisDisplay.vue */
  DISPLAY_PRODUCT_ANALYSIS: createCanvasActionHandler(
    'DISPLAY_PRODUCT_ANALYSIS',
    'Display product analysis in Purchase Assistant canvas',
  ),

  /** Display recommendation explanation - Target: RecommendationExplanationDisplay.vue */
  DISPLAY_RECOMMENDATION_EXPLANATION: createCanvasActionHandler(
    'DISPLAY_RECOMMENDATION_EXPLANATION',
    'Display recommendation explanation in Purchase Assistant canvas',
  ),

  /** Display PO review results with analysis - Target: POReviewResultsDisplay.vue */
  DISPLAY_PO_REVIEW_RESULTS: createCanvasActionHandler(
    'DISPLAY_PO_REVIEW_RESULTS',
    'Display PO review results with MOV validation, stockout risk, and recommendations',
  ),

  /**
   * PO Agent: Confirm and create purchase order after AI review
   *
   * Shown after review_po_items analysis. Provides a confirmation button
   * that the user can click to create the PO, or they can type "create"
   * in the chat.
   *
   * **Target Component**: PurchaseAssistant.vue (renders confirmation button in chat or canvas)
   * **Backend Agent**: Purchase Order Agent
   *
   * @param payload - { supplier_id, supplier_name, total_items, total_value, items }
   */
  CONFIRM_CREATE_PO: {
    type: 'CONFIRM_CREATE_PO',
    isCanvasAction: false, // This shows a confirmation button in chat, not canvas
    isControlMessage: false,
    handler: (payload: any, context: ChatContext) => {
      console.log('[AgentActions] CONFIRM_CREATE_PO', {
        agent: context.agentId,
        supplier_id: payload.supplier_id,
        supplier_name: payload.supplier_name,
        total_items: payload.total_items,
        total_value: payload.total_value,
      });

      // Emit event for POBuilder to show confirmation button
      EventBus.$emit('po-builder:confirm-create', {
        supplierId: payload.supplier_id,
        supplierName: payload.supplier_name,
        totalItems: payload.total_items,
        totalValue: payload.total_value,
        items: payload.items,
        sessionId: context.sessionId,
      });

      // Track analytics event
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('PO Confirmation Shown', {
          agent: context.agentId,
          supplier_id: payload.supplier_id,
          total_items: payload.total_items,
          total_value: payload.total_value,
        });
      }
    },
    description: 'Show confirmation button to create PO after AI review',
  },

  /**
   * Customer Service: Trigger human support handoff
   *
   * **Emits**: EventBus event 'support:handoff-requested'
   * **Target Component**: Various (handoff banner, Crisp chat integration)
   * **Backend Agent**: Customer Service Agent
   *
   * @param payload - HandoffPayload with needs_human_handoff flag
   * @param context - ChatContext with session and agent info
   */
  trigger_handoff: {
    type: 'trigger_handoff',
    loadingMessage: 'Connecting you to support...',
    isCanvasAction: false,
    isControlMessage: true,
    handler: (payload: HandoffPayload, context: ChatContext) => {
      console.log('[AgentActions] Executing trigger_handoff', {
        agent: context.agentId,
        session: context.sessionId,
        reason: payload.reason,
      });

      // Emit EventBus event to open Crisp chat (listened by ChatConversation.vue)
      EventBus.$emit('open-crisp-for-handoff', {
        agentId: context.agentId,
        agentName: context.agentId, // You may want to pass the actual agent name
        conversationId: context.sessionId,
        timestamp: new Date().toISOString(),
        metadata: {
          handoff_reason: payload.reason || 'agent_triggered_handoff',
          conversation_context: payload.conversation_context,
          issue_category: payload.issue_category,
        },
      });

      // Also emit for any other components that need to know about handoff
      EventBus.$emit('support:handoff-requested', {
        sessionId: context.sessionId,
        agentId: context.agentId,
        reason: payload.reason,
        timestamp: new Date().toISOString(),
        conversationContext: payload.conversation_context,
        issueCategory: payload.issue_category,
      });

      // Update UI state flag if context allows
      if (context.showHandoffBanner !== undefined) {
        context.showHandoffBanner = true;
      }

      // Track analytics event
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('Support Handoff Triggered', {
          agent: context.agentId,
          reason: payload.reason,
          session: context.sessionId,
        });
      }
    },
    description: 'Initiate human support handoff flow and open Crisp chat',
  },

  /**
   * Future: Chart Builder - Render interactive chart
   *
   * **Emits**: EventBus event 'chart:render'
   * **Target Component**: ChartViewer.vue (future)
   * **Backend Agent**: Chart Builder Agent (future)
   *
   * @param payload - ChartPayload with chart type, data, and options
   * @param context - ChatContext with session and agent info
   */
  render_chart: {
    type: 'render_chart',
    loadingMessage: 'Rendering chart...',
    isCanvasAction: true,
    isControlMessage: false,
    handler: (payload: ChartPayload, context: ChatContext) => {
      console.log('[AgentActions] Executing render_chart', {
        agent: context.agentId,
        chart_type: payload.chart_type,
      });

      // Emit EventBus event for chart rendering component
      EventBus.$emit('chart:render', {
        type: payload.chart_type,
        data: payload.data,
        options: payload.options,
        sessionId: context.sessionId,
      });

      // Track analytics
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('Chart Rendered', {
          agent: context.agentId,
          chart_type: payload.chart_type,
          data_points: payload.data?.length || 0,
        });
      }
    },
    description: 'Render interactive chart visualization (future feature)',
  },

  /**
   * Future: Form Builder - Show dynamic form
   *
   * **Emits**: EventBus event 'form:display'
   * **Target Component**: DynamicFormViewer.vue (future)
   * **Backend Agent**: Form Builder Agent (future)
   *
   * @param payload - FormPayload with form schema
   * @param context - ChatContext with session and agent info
   */
  show_dynamic_form: {
    type: 'show_dynamic_form',
    loadingMessage: 'Loading form...',
    isCanvasAction: true,
    isControlMessage: false,
    handler: (payload: FormPayload, context: ChatContext) => {
      console.log('[AgentActions] Executing show_dynamic_form', {
        agent: context.agentId,
        field_count: payload.form_schema?.fields?.length || 0,
      });

      // Emit EventBus event for form display component
      EventBus.$emit('form:display', {
        schema: payload.form_schema,
        sessionId: context.sessionId,
      });

      // Track analytics
      if ((window as any).mixpanel) {
        (window as any).mixpanel.track('Dynamic Form Displayed', {
          agent: context.agentId,
          field_count: payload.form_schema?.fields?.length || 0,
        });
      }
    },
    description: 'Display dynamically generated form (future feature)',
  },
};

/**
 * Execute an agent action with proper error handling and logging
 *
 * This is the main entry point for executing agent actions.
 * It validates the action exists, executes the handler, and logs appropriately.
 *
 * @param action - Action identifier from metadata.action
 * @param payload - Action-specific payload data from metadata.payload
 * @param context - Execution context with session, agent, and user info
 * @returns true if action was executed successfully, false otherwise
 *
 * @example
 * ```typescript
 * const success = executeAgentAction(
 *   'show_report_preview',
 *   payload,
 *   {
 *     sessionId: 'conv_123',
 *     agentId: 'report-builder',
 *     userId: 'user_456'
 *   }
 * );
 * ```
 */
export function executeAgentAction(
  action: string,
  payload: any,
  context: ChatContext,
): boolean {
  // Validate action exists in registry
  const actionHandler = agentActionHandlers[action];

  if (!actionHandler) {
    console.warn(
      `[AgentActions] No handler registered for action: "${action}"`,
      {
        available_actions: getRegisteredActions(),
        agent: context.agentId,
        session: context.sessionId,
      },
    );
    return false;
  }

  try {
    // Execute the handler function
    actionHandler.handler(payload, context);

    console.log(
      `[AgentActions] Successfully executed action: "${action}"`,
      {
        agent: context.agentId,
        session: context.sessionId,
        description: actionHandler.description,
      },
    );

    return true;
  } catch (error) {
    console.error(
      `[AgentActions] Error executing action "${action}":`,
      error,
      {
        agent: context.agentId,
        session: context.sessionId,
        payload_preview: JSON.stringify(payload).substring(0, 200),
      },
    );
    return false;
  }
}

/**
 * Get all registered action types
 *
 * Useful for debugging and validation.
 *
 * @returns Array of action type strings
 *
 * @example
 * ```typescript
 * const actions = getRegisteredActions();
 * // ['show_report_preview', 'trigger_handoff', ...]
 * ```
 */
export function getRegisteredActions(): string[] {
  return Object.keys(agentActionHandlers);
}

/**
 * Check if an action is registered
 *
 * @param action - Action identifier to check
 * @returns true if action has a registered handler
 *
 * @example
 * ```typescript
 * if (isActionRegistered('show_report_preview')) {
 *   // Action exists and can be executed
 * }
 * ```
 */
export function isActionRegistered(action: string): boolean {
  return action in agentActionHandlers;
}

/**
 * Get handler description for an action
 *
 * Useful for debugging and documentation.
 *
 * @param action - Action identifier
 * @returns Description string or undefined if action not found
 *
 * @example
 * ```typescript
 * const description = getActionDescription('show_report_preview');
 * // 'Display report preview with extracted parameters...'
 * ```
 */
export function getActionDescription(action: string): string | undefined {
  return agentActionHandlers[action]?.description;
}

/**
 * Get all actions with their descriptions
 *
 * Returns a map of action identifiers to their descriptions.
 * Useful for generating documentation or debugging.
 *
 * @returns Record mapping action identifiers to descriptions
 *
 * @example
 * ```typescript
 * const actionDocs = getAllActionDescriptions();
 * // {
 * //   'show_report_preview': 'Display report preview...',
 * //   'trigger_handoff': 'Initiate human support...',
 * //   ...
 * // }
 * ```
 */
export function getAllActionDescriptions(): Record<string, string> {
  const descriptions: Record<string, string> = {};

  Object.entries(agentActionHandlers).forEach(([action, handler]) => {
    if (handler.description) {
      descriptions[action] = handler.description;
    }
  });

  return descriptions;
}

/**
 * Validate metadata structure before executing action
 *
 * Performs type checking and validation on metadata object.
 *
 * @param metadata - Metadata object to validate
 * @returns Validation result with success flag and optional error message
 *
 * @example
 * ```typescript
 * const result = validateMetadata(response.metadata);
 * if (result.valid) {
 *   executeAgentAction(metadata.action, metadata.payload, context);
 * } else {
 *   console.error(result.error);
 * }
 * ```
 */
export function validateMetadata(metadata: any): {
  valid: boolean;
  error?: string;
} {
  // Check if metadata exists
  if (!metadata) {
    return {
      valid: false,
      error: 'Metadata is null or undefined',
    };
  }

  // Check if metadata is an object
  if (typeof metadata !== 'object') {
    return {
      valid: false,
      error: `Metadata must be an object, got ${typeof metadata}`,
    };
  }

  // Check if action field exists
  if (!('action' in metadata)) {
    return {
      valid: false,
      error: 'Metadata missing required "action" field',
    };
  }

  // Check if action is a string
  if (typeof metadata.action !== 'string') {
    return {
      valid: false,
      error: `Metadata.action must be a string, got ${typeof metadata.action}`,
    };
  }

  // Check if action is registered
  if (!isActionRegistered(metadata.action)) {
    return {
      valid: false,
      error: `Action "${metadata.action}" is not registered. Available: ${getRegisteredActions().join(', ')}`,
    };
  }

  // Check if payload exists
  if (!('payload' in metadata)) {
    return {
      valid: false,
      error: 'Metadata missing required "payload" field',
    };
  }

  return { valid: true };
}

// ===========================================
// Classification-Based Helper Functions
// ===========================================

/**
 * Get all action types that are canvas actions
 *
 * Canvas actions display components in the left canvas panel.
 * Used by AgentChatbox to determine which actions trigger canvas updates.
 *
 * @returns Array of action type strings that are canvas actions
 *
 * @example
 * ```typescript
 * const canvasActions = getCanvasActionTypes();
 * // ['DISPLAY_BUYING_TABLE', 'DISPLAY_SUPPLIERS_LIST', 'SHOW_REPORT_PREVIEW', ...]
 * ```
 */
export function getCanvasActionTypes(): string[] {
  return Object.entries(agentActionHandlers)
    .filter(([_, handler]) => handler.isCanvasAction === true)
    .map(([action]) => action);
}

/**
 * Get all action types that are control messages
 *
 * Control messages should be filtered from chat display.
 * These include tool outputs, internal state updates, and action confirmations.
 *
 * @returns Array of action type strings that are control messages
 *
 * @example
 * ```typescript
 * const controlMessages = getControlMessageTypes();
 * // ['trigger_handoff', ...]
 * ```
 */
export function getControlMessageTypes(): string[] {
  return Object.entries(agentActionHandlers)
    .filter(([_, handler]) => handler.isControlMessage === true)
    .map(([action]) => action);
}

/**
 * Check if an action is a canvas action
 *
 * @param action - Action identifier to check
 * @returns true if action displays in canvas panel
 *
 * @example
 * ```typescript
 * if (isCanvasAction('DISPLAY_BUYING_TABLE')) {
 *   // This action will update the canvas panel
 * }
 * ```
 */
export function isCanvasAction(action: string): boolean {
  return agentActionHandlers[action]?.isCanvasAction === true;
}

/**
 * Check if an action is a control message
 *
 * @param action - Action identifier to check
 * @returns true if action should be filtered from chat display
 *
 * @example
 * ```typescript
 * if (isControlMessage('trigger_handoff')) {
 *   // Filter this message from chat display
 * }
 * ```
 */
export function isControlMessage(action: string): boolean {
  return agentActionHandlers[action]?.isControlMessage === true;
}

/**
 * Extract action code from an event object
 *
 * Handles the standardized event format where:
 * - type: "action"
 * - content: ACTION_CODE (e.g., "DISPLAY_BUYING_TABLE")
 * - metadata.action: ACTION_CODE (same as content)
 * - metadata.parameters: Action-specific parameters
 *
 * @param event - Event object from agent response
 * @returns Action code string or null if not an action event
 *
 * @example
 * ```typescript
 * const actionCode = extractActionCode(event);
 * if (actionCode && isCanvasAction(actionCode)) {
 *   // Process canvas action
 * }
 * ```
 */
export function extractActionCode(event: any): string | null {
  if (!event || event.type !== 'action') {
    return null;
  }

  // Prefer metadata.action, fallback to content
  return event.metadata?.action || event.content || null;
}

/**
 * Extract action parameters from an event object
 *
 * @param event - Event object from agent response
 * @returns Action parameters object or empty object if not found
 *
 * @example
 * ```typescript
 * const params = extractActionParameters(event);
 * // { suppliers: [...], total_count: 5 }
 * ```
 */
export function extractActionParameters(event: any): any {
  if (!event || event.type !== 'action') {
    return {};
  }

  return event.metadata?.parameters || {};
}

/**
 * Get all canvas action types from events array
 *
 * Useful for determining which canvas components to display
 * based on the events in a chat response.
 *
 * @param events - Array of event objects from chat response
 * @returns Array of unique canvas action types found in events
 *
 * @example
 * ```typescript
 * const canvasActions = getCanvasActionsFromEvents(response.events);
 * // ['DISPLAY_BUYING_TABLE', 'DISPLAY_SUPPLIERS_LIST']
 * ```
 */
export function getCanvasActionsFromEvents(events: any[]): string[] {
  if (!events || !Array.isArray(events)) {
    return [];
  }

  const actionCodes = events
    .filter((e) => e.type === 'action')
    .map((e) => extractActionCode(e))
    .filter((code): code is string => code !== null && isCanvasAction(code));

  // Return unique action codes
  return [...new Set(actionCodes)];
}
